import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogComponent } from '@klickdata/core/application';
import { AuthService } from '@klickdata/core/auth';
import { MessageDeletedComponent, MessageService } from '@klickdata/core/message';
import { MobileService } from '@klickdata/core/mobile';
import { ResourceTypes } from '@klickdata/core/resource';
import { Section, SectionPublicOrder, SectionService } from '@klickdata/core/section';
import { GlobalFilterProperty, SelectFilterOption } from '@klickdata/core/table';
import { Utils } from '@klickdata/core/util';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { BrowseFolderComponent } from '../../folder/browse-folder/browse-folder.component';
import { SelectModelBaseDirective } from '../select-model-base/select-model-base.component';
import { SectionSelectDatatableService } from './section-select-datatable.service';
import { Observable } from 'rxjs';
import { Customer } from '@klickdata/core/customer';

@Component({
    selector: 'app-section-select',
    templateUrl: './section-select.component.html',
    styleUrls: ['./section-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SectionSelectDatatableService],
})
export class SectionSelectComponent
    extends SelectModelBaseDirective<Section>
    implements OnInit, AfterViewInit, OnDestroy
{
    @Input() accessForm: FormGroup;
    @Input() referenceFilter: string;
    @Input() scope: 'public' | 'assignedOrCustomer' | 'customer' | null = 'assignedOrCustomer';
    @Input() hideTools: boolean;
    @Input() canDrag: boolean;
    @Input() customer$: Observable<Customer>;
    @Input() params: { property: string; items: any[] }[];
    @Output() add: EventEmitter<void> = new EventEmitter<void>();
    @Output() edit: EventEmitter<Section> = new EventEmitter<Section>();
    ResourceTypes = ResourceTypes;
    @Input() sync_all_key = 'sync_all_sections';
    @Input() attachKey = 'sections_attach';
    @Input() detachKey = 'sections_detach';
    @Input() eager: string[] = [];
    @Input() columns: string[];
    public itemSelectedOptions: SelectFilterOption[];
    GlobalFilterProperty = GlobalFilterProperty;
    public isMasterAdmin$: Observable<boolean>;

    constructor(
        public datatableService: SectionSelectDatatableService,
        protected bottomSheet: MatBottomSheet,
        protected mobileService: MobileService,
        protected sectionService: SectionService,
        protected dialog: MatDialog,
        protected auth: AuthService,
        protected messageService: MessageService,
        protected cdRef: ChangeDetectorRef
    ) {
        super(datatableService, mobileService);
        this.itemSelectedOptions = Utils.getItemSelectedOptions();
        this.isMasterAdmin$ = this.auth.getUser().pipe(
            first(),
            map((user) => user.isMasterAdmin())
        );
    }

    /**
     *
     */
    public ngOnInit(): void {
        super.ngOnInit();
        this.columns = this.columns
            ? this.columns
            : this.hideTools
            ? ['select', 'title', 'updated_at']
            : ['select', 'title', 'updated_at', 'public_order', 'tools'];
    }
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        if (!this.accessForm.value.id && !!this.params?.length) {
            this.filter.createOrUpdateFilter(this.params);
        }
    }

    protected refresh(id: any): void {
        if (this.scope === 'public') {
            const filters = [
                { property: this.referenceFilter, items: [id] },
                { property: 'referenceCount', items: [1] },
                { property: this.scope, items: [1] },
            ];
            if (!!this.eager?.length) {
                filters.push({ property: 'eager', items: this.eager });
            }
            this.filter.createOrUpdateFilter(filters);
        } else {
            const customerSource$ = this.customer$ ?? this.auth.getCustomer();

            customerSource$.pipe(first(), takeUntil(this.destroy)).subscribe((customer) => {
                const filters = [
                    { property: this.referenceFilter, items: [id] },
                    { property: 'referenceCount', items: [1] },
                ];
                if (!!this.scope) {
                    filters.push({
                        property: this.scope,
                        items: [customer.id],
                    });
                }
                if (!!this.eager?.length) {
                    filters.push({ property: 'eager', items: this.eager });
                }
                this.filter.createOrUpdateFilter(
                    this.params && this.params.length ? [...filters, ...this.params] : [...filters]
                );
            });
        }
    }

    public showResource(section: Section) {
        this.bottomSheet.open(BrowseFolderComponent, {
            data: section,
            panelClass: 'sheet-wrapper',
        });
    }

    public deleteSection(section: Section) {
        this.dialog
            .open(DeleteDialogComponent)
            .afterClosed()
            .pipe(
                takeUntil(this.destroy),
                filter((result) => result),
                switchMap(() => this.sectionService.destroy(section))
            )
            .subscribe((success) => {
                if (success) {
                    this.dataSource.refresh();
                    this.messageService.openMessage(MessageDeletedComponent);
                }
            });
    }

    drop(event: CdkDragDrop<Section[]>) {
        this.dataSource.moveItemInArray(event.previousIndex, event.currentIndex);
        const section = this.dataSource.data[event.currentIndex];
        if (
            section.updateOrder(
                this.dataSource.data,
                event.currentIndex,
                event.previousIndex,
                SectionPublicOrder.getInstance()
            )
        )
            this.updatePriority(section);
    }

    private updatePriority(section: Section) {
        this.sectionService
            .update({
                id: section.id,
                customer_id: <number>this.filter.getFilterValue(this.referenceFilter)[0],
                public_order: section.public_order,
            })
            .pipe(takeUntil(this.destroy), first())
            .subscribe();
    }
}
